/**
 * The webhooks console — `/v1/admin/webhooks`.
 *
 * **"What did this webhook do" is a query, not a column.** The inbound record is immutable by
 * design, so outcomes are read from the payment events pointing back at it. That is why a row
 * carries `outcome_count` rather than a status: nobody can edit a count of things that happened.
 *
 * **Reprocessing invalidates the payments reads too.** A replayed capture can confirm a booking
 * and move an order to `paid`; a console that refreshed the webhook list and not the payments
 * browser would show the two screens disagreeing about the same event.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authedClient } from '../../auth/session';
import { keys } from '../queryClient';
import type { components } from '../../../api/generated/schema';

export type WebhookRow = components['schemas']['WebhookRow'];
export type WebhookOutcomeRow = components['schemas']['WebhookOutcomeRow'];
type WebhookPage = components['schemas']['WebhookPage'];
type WebhookDetail = components['schemas']['WebhookDetail'];
type WebhookReprocessed = components['schemas']['WebhookReprocessed'];

export interface WebhookQuery {
  resolved?: string;
  event_name?: string;
  signature?: string;
  handled?: string;
  q?: string;
  page?: number;
  page_size?: number;
}

/** Only what the operator set — an empty `signature=` would narrow to nothing. */
export function webhookParams(query: WebhookQuery): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  return search.toString();
}

export function useWebhooks(query: WebhookQuery) {
  const params = webhookParams(query);
  return useQuery({
    queryKey: keys.webhooks.list(query as Readonly<Record<string, unknown>>),
    queryFn: () => authedClient.request<WebhookPage>(`/v1/admin/webhooks?${params}`),
  });
}

export function useWebhookDetail(eventId: number | null) {
  return useQuery({
    queryKey: keys.webhooks.one(eventId ?? 0),
    queryFn: () => authedClient.request<WebhookDetail>(`/v1/admin/webhooks/${eventId}`),
    enabled: eventId !== null,
  });
}

/**
 * Declaring an event dealt with.
 *
 * **The inbound record is never modified** — the resolution is an audit row, which is why this
 * takes a mandatory reason. "Resolved" with no reason tells the next person nothing, and the next
 * person is usually reading the queue at 3am wondering whether this one was handled or dismissed.
 */
export function useResolveWebhook() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ eventId, reason }: { eventId: number; reason: string }) =>
      authedClient.request<{ event_id: number; resolved: boolean; reason: string }>(
        `/v1/admin/webhooks/${eventId}/resolve`,
        { method: 'POST', body: { reason } },
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.webhooks.all() }),
  });
}

export function useReprocessWebhook() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (eventId: number) =>
      authedClient.request<WebhookReprocessed>(
        `/v1/admin/webhooks/${eventId}/reprocess`,
        { method: 'POST' },
      ),
    onSuccess: () => {
      // Both, and for a reason: a replayed capture can confirm a booking and move an order to
      // `paid`. Refreshing one screen and not the other shows them disagreeing.
      void queryClient.invalidateQueries({ queryKey: keys.webhooks.all() });
      void queryClient.invalidateQueries({ queryKey: ['payments'] });
    },
  });
}
